import { useEffect } from 'react';
import { useSession } from '../contexts/SessionContext';
import { setSessionHandlers } from '../utils/axiosInterceptor';

export const useSessionManager = () => {
  const { handleSessionExpiry, handleUnauthorized } = useSession();

  useEffect(() => {
    // Initialize axios interceptors with session handlers
    setSessionHandlers(handleSessionExpiry, handleUnauthorized);

    // Optional: Set up periodic token validation
    const validateSession = () => {
      // Don't validate if we're on the login page
      if (window.location.pathname === '/') {
        return;
      }
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        // Only trigger session expiry if we're not already on login page
        if (window.location.pathname !== '/') {
          handleSessionExpiry();
        }
        return;
      }

      // Optional: Check if token is expired by parsing JWT
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp && payload.exp < currentTime) {
          // Only trigger session expiry if we're not already on login page
          if (window.location.pathname !== '/') {
            handleSessionExpiry();
          }
        }
      } catch (error) {
        // If token parsing fails, it might be invalid
        console.warn('Invalid token format detected');
      }
    };

    // Validate session immediately only if not on login page
    if (window.location.pathname !== '/') {
      validateSession();
    }

    // Set up periodic validation (every 5 minutes)
    const interval = setInterval(validateSession, 5 * 60 * 1000);

    // Listen for storage changes (in case user logs out in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken' && !e.newValue && window.location.pathname !== '/') {
        // Token was removed in another tab, only trigger if not on login page
        handleSessionExpiry();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleSessionExpiry, handleUnauthorized]);
};